import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import type { Screen } from "@/lib/types";
import { StatusPill, screenState } from "@/components/app/StatusPill";

function AddScreensDialog({ open, onOpenChange, candidates, onAdd }: { open: boolean; onOpenChange: (o: boolean) => void; candidates: Screen[]; onAdd: (ids: string[]) => Promise<void> }) {
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const toggle = (id: string) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const submit = async () => { setBusy(true); await onAdd(selected); setBusy(false); setSelected([]); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="group-add-screens-dialog" className="border-slate-800 bg-slate-900">
        <DialogHeader>
          <DialogTitle>Ajouter des téléviseurs au groupe</DialogTitle>
          <DialogDescription className="text-slate-400">Les écrans choisis hériteront de la playlist, de l'horaire, du bandeau et de l'horloge du groupe.</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 space-y-2 overflow-y-auto">
          {candidates.map((s) => (
            <label key={s.id} data-testid={`group-add-candidate-${s.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2.5 hover:border-slate-700">
              <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`group-add-checkbox-${s.id}`} />
              <div className="min-w-0 flex-1"><p className="truncate text-sm text-slate-200">{s.name}</p><p className="text-xs text-slate-500">{s.group_name ? `Actuellement dans « ${s.group_name} »` : "Sans groupe"}</p></div>
              <StatusPill state={screenState(s)} />
            </label>
          ))}
          {!candidates.length && <p className="text-sm text-slate-500">Tous vos écrans actifs sont déjà dans ce groupe. Jumelez un nouveau téléviseur pour en ajouter.</p>}
        </div>
        <Button data-testid="group-add-screens-submit" disabled={!selected.length || busy} onClick={submit} className="bg-blue-600 text-white hover:bg-blue-500">
          {busy ? "Ajout…" : `Ajouter ${selected.length || ""} écran(s)`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export function GroupScreensPanel({ groupId, screens, onChange }: { groupId: string; screens: Screen[]; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const active = screens.filter((s) => s.status === "active");
  const members = active.filter((s) => s.group_id === groupId);
  const candidates = active.filter((s) => s.group_id !== groupId);

  const add = async (ids: string[]) => {
    try {
      await Promise.all(ids.map((id) => api.patch(`/screens/${id}`, { group_id: groupId, use_group_config: true })));
      toast.success(`${ids.length} écran(s) ajouté(s) au groupe`);
      setOpen(false); onChange();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const remove = async (s: Screen) => {
    try { await api.patch(`/screens/${s.id}`, { group_id: "", use_group_config: false }); toast.success(`« ${s.name} » retiré du groupe`); onChange(); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <p className="eyebrow">Écrans du groupe ({members.length})</p>
        <Button size="sm" data-testid="btn-group-add-screens" onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-500"><Plus className="mr-1 h-4 w-4" />Ajouter</Button>
      </div>
      <div className="space-y-2" data-testid="group-screens-list">
        {members.map((s) => (
          <div key={s.id} data-testid={`group-member-${s.id}`} className="flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2 text-sm">
            <Link to={`/ecrans/${s.id}`} className="min-w-0 flex-1 space-y-1">
              <span className="block truncate text-slate-200 hover:text-blue-300">{s.name}{!s.use_group_config && <span className="ml-1 text-[10px] text-amber-400">(config. propre)</span>}</span>
              <StatusPill state={screenState(s)} />
            </Link>
            <Button variant="ghost" size="icon" data-testid={`group-remove-screen-${s.id}`} title="Retirer du groupe" onClick={() => remove(s)} className="h-7 w-7 text-slate-500 hover:text-rose-400"><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {!members.length && <p className="text-sm text-slate-500">Aucun écran. Cliquez sur « Ajouter » pour y placer vos téléviseurs.</p>}
      </div>
      <AddScreensDialog open={open} onOpenChange={setOpen} candidates={candidates} onAdd={add} />
    </>
  );
}
